/**
 * Auto-Login Reload Guard
 *
 * Prevents infinite reload loops when auto-login (OfficeRND iframe) fails repeatedly.
 * Uses sessionStorage to track reload attempts across page reloads (sessionStorage
 * survives reloads but not tab close, which is exactly what we need).
 *
 * If more than MAX_RETRIES reloads happen within WINDOW_MS, further reloads are blocked.
 */

const MAX_RETRIES = 3;
const WINDOW_MS = 5 * 60 * 1000; // 5 minutes
const KEY = 'autoLoginRetries';

/**
 * Check whether another auto-login reload is allowed.
 * @returns {boolean} true if reload is safe, false if limit reached
 */
export function canAutoLoginReload() {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return true;

  const { count, firstAttempt } = JSON.parse(raw);

  // Window expired — treat as fresh start
  if (Date.now() - firstAttempt > WINDOW_MS) return true;

  return count < MAX_RETRIES;
}

/**
 * Record that an auto-login reload is about to happen.
 * Must be called immediately before window.location.reload().
 */
export function recordAutoLoginReload() {
  const raw = sessionStorage.getItem(KEY);
  const now = Date.now();

  if (!raw || now - JSON.parse(raw).firstAttempt > WINDOW_MS) {
    // No prior record or window expired — start fresh
    sessionStorage.setItem(KEY, JSON.stringify({ count: 1, firstAttempt: now }));
  } else {
    const data = JSON.parse(raw);
    data.count += 1;
    sessionStorage.setItem(KEY, JSON.stringify(data));
  }
}

/**
 * Reset the reload counter.
 * Call this after a successful auto-login so that the next session-expiry
 * gets a fresh set of retries.
 */
export function resetAutoLoginRetries() {
  sessionStorage.removeItem(KEY);
}
